/**
 * Phase 93 (Plan 93-01, Task 1) — modules API client.
 *
 * Backend contract: `GET /api/modules` (`authenticate`-only,
 * `backend/src/api/routes/modules/core.ts:306-326`) — `{ data: { enabled: string[] } }`,
 * a sorted array of the currently-enabled module ids for this deployment.
 *
 * `MODULE_IDS` below is hand-synced with `backend/src/config/modules.ts` and
 * has NO automated cross-package drift check today — update both in the same
 * commit. A frontend test (`modulesContext.test.tsx`) asserts `MODULE_IDS`
 * has exactly 13 entries as a cheap regression guard.
 */
import { apiGet } from './client';

/** The 13 modules, hand-synced with `backend/src/config/modules.ts:23-37`. */
export const MODULE_IDS = [
  'core',
  'time',
  'billing',
  'projects',
  'workmgmt',
  'portal',
  'people',
  'capacity',
  'analytics',
  'monka',
  'isms',
  'payroll',
  'sales',
] as const;

export type ModuleId = (typeof MODULE_IDS)[number];

export interface ModulesResponse {
  enabled: ModuleId[];
}

function isModuleId(token: string): token is ModuleId {
  return (MODULE_IDS as readonly string[]).includes(token);
}

/**
 * Fetches the enabled-module list and filters it against `MODULE_IDS` before
 * returning (ASVS V5 — an unknown/hostile id string must never reach a nav
 * map or `hasModule` set).
 */
export async function getModules(): Promise<ModulesResponse> {
  const res = await apiGet<{ data: { enabled: string[] } }>('/modules');
  return { enabled: res.data.enabled.filter(isModuleId) };
}
